// ============================================================================
// Beacon — third-party data providers (the "wire everything up" layer).
//
// Every fact Beacon can pull about a property or its area comes through a
// DataProvider: a small, uniform adapter that declares WHAT it fills on a Deal,
// WHAT it costs, WHERE it must run (browser vs. server), and HOW it's cited.
//
// Design reality (verified against the live sources):
//   • US Census GEOCODER  — free, no key  -> runs in browser (via /geo proxy)
//   • US Census ACS       — free, needs a free key -> browser if key present
//   • HUD FMR / IL        — free, needs a token     -> Edge Function (or dev key)
//   • FL statewide parcel — free, no key, but too slow/rate-limited to hit live
//                           -> Edge Function + `data_cache` (server-side)
//   • Crime / mortgages / liens / rent comps / AVM — mostly PAID aggregators
//                           -> Edge Function with the vendor key
//
// So the browser holds the framework + the free/keyed reads; the heavy and
// paid sources resolve in a Supabase Edge Function that caches into
// `data_cache` (source + pull-date attribution — see the PRD's citation rule).
// ============================================================================

import type { Deal } from "../types";

export type ProviderCost = "free" | "free-key" | "token" | "paid";
export type RunsIn = "browser" | "edge";
export type ProviderStatus = "live" | "needs-key" | "edge" | "paid";

export interface DataProvider {
  id: string;
  label: string;
  category:
    | "geo"
    | "assessment" // property appraiser: assessed/just/taxable value, exemptions
    | "ownership" // owner, mortgages, liens, sales history
    | "demographic"
    | "income"
    | "rental"
    | "risk" // flood / crime / environmental
    | "designation";
  cost: ProviderCost;
  runsIn: RunsIn;
  keyEnv?: keyof ImportMetaEnv; // env var that unlocks it in the browser
  fills: string[]; // Deal fields / facts this source populates
  what: string; // one-line plain-English value
  source: string; // authoritative publisher
  url: string; // docs / signup / map
  fairHousingNote?: string; // when a source touches protected-class or crime data
}

const env = import.meta.env;

/** The catalog. Order = rough build priority (free & high-impact first). */
export const PROVIDERS: DataProvider[] = [
  {
    id: "census-geocoder",
    label: "US Census Geocoder",
    category: "geo",
    cost: "free",
    runsIn: "browser",
    fills: ["censusTract", "lat/long (for parcel lookup)"],
    what: "Turns a street address into the census tract + point that every other source is keyed by.",
    source: "US Census Bureau",
    url: "https://geocoding.geo.census.gov/geocoder/",
  },
  {
    id: "fl-parcel",
    label: "FL Statewide Parcels (DOR)",
    category: "assessment",
    cost: "free",
    runsIn: "edge",
    fills: ["taxes (from just value × millage)", "purchase sanity (last sale)", "yearBuilt", "unit count", "assessed/just/taxable value", "homestead flag"],
    what: "Property-appraiser assessments for all 67 counties: just/assessed/taxable value, exemptions, last two sales, year built, units, use code. Free but too large to query live — cached server-side.",
    source: "FL Dept. of Revenue — Statewide Cadastral",
    url: "https://services9.arcgis.com/Gh9awoU677aKree0/arcgis/rest/services/Florida_Statewide_Cadastral/FeatureServer/0",
  },
  {
    id: "census-acs",
    label: "Census ACS (demographics & income)",
    category: "demographic",
    cost: "free-key",
    runsIn: "browser",
    keyEnv: "VITE_CENSUS_KEY",
    fills: ["popGrow context", "renterRising", "median income (AMI context)", "median gross rent"],
    what: "Median household income, renter share, population and median rent for the tract — the location & income signals behind the score.",
    source: "US Census Bureau — ACS 5-year",
    url: "https://api.census.gov/data/key_signup.html",
    fairHousingNote: "Use for market/underwriting context only — never to screen tenants or steer by protected class.",
  },
  {
    id: "hud-fmr",
    label: "HUD FMR / Income Limits",
    category: "rental",
    cost: "token",
    runsIn: "edge",
    keyEnv: "VITE_HUD_TOKEN",
    fills: ["safmr2 (real SAFMR by ZIP)", "AMI / income limits", "payment-standard basis"],
    what: "The actual Small Area Fair Market Rent and Income Limits that set your voucher rent ceiling — currently entered by hand.",
    source: "HUD USER API",
    url: "https://www.huduser.gov/portal/dataset/fmr-api.html",
  },
  {
    id: "fema-nfhl",
    label: "FEMA Flood (NFHL)",
    category: "risk",
    cost: "free",
    runsIn: "browser",
    fills: ["flood (zone at the point)"],
    what: "The FEMA flood zone for the parcel — a first-order Florida insurance driver already in the model.",
    source: "FEMA National Flood Hazard Layer",
    url: "https://hazards.fema.gov/femaportal/wps/portal/NFHLWMS",
  },
  {
    id: "fbi-crime",
    label: "FBI Crime Data",
    category: "risk",
    cost: "free-key",
    runsIn: "edge",
    fills: ["neighborhood safety context"],
    what: "Agency-level violent/property crime trends for location context. Coarse (agency, not block) — a market signal, not a screening tool.",
    source: "FBI Crime Data Explorer API",
    url: "https://cde.ucr.cjis.gov/",
    fairHousingNote: "Crime data must not drive tenant screening or steering; use only for market/location analysis.",
  },
  {
    id: "attom",
    label: "ATTOM / CoreLogic (deeds, mortgages, liens)",
    category: "ownership",
    cost: "paid",
    runsIn: "edge",
    fills: ["lisPendens + openLiens (→ Gate D)", "outstanding mortgages", "AVM / seller equity", "full sales history", "owner portfolio"],
    what: "Recorded mortgages, open liens, pre-foreclosure (lis pendens), automated valuation, and owner portfolio — the seller-motivation and title-risk picture. Feeds the legal/title gate.",
    source: "ATTOM Data / CoreLogic (subscription)",
    url: "https://api.developer.attomdata.com/",
  },
  {
    id: "rentcast",
    label: "RentCast / HelloData (rent comps)",
    category: "rental",
    cost: "paid",
    runsIn: "edge",
    fills: ["comps (→ comps strength)", "market rent vs. contract rent (rent reasonableness)", "rent range"],
    what: "Address-level market-rent comps to validate underwritten rents against real listings and support the HUD rent-reasonableness test.",
    source: "RentCast / HelloData (subscription)",
    url: "https://www.rentcast.io/api",
  },
  {
    id: "regrid",
    label: "Regrid (nationwide parcels)",
    category: "assessment",
    cost: "paid",
    runsIn: "edge",
    fills: ["parcel + assessment outside FL", "standardized schema", "owner mailing (absentee)"],
    what: "A clean, nationwide parcel/assessment API — the paid alternative when you expand past Florida and don't want 67 county schemas.",
    source: "Regrid (subscription)",
    url: "https://regrid.com/api",
  },
];

/** Is a provider actually usable from the browser right now? */
export function providerStatus(p: DataProvider): ProviderStatus {
  if (p.runsIn === "edge") return p.cost === "paid" ? "paid" : "edge";
  if (p.cost === "free") return "live";
  // free-key / token that can run in the browser: live only if the key is set
  return p.keyEnv && env[p.keyEnv] ? "live" : "needs-key";
}

// ---------------------------------------------------------------------------
// Census ACS — area demographics & income (the one keyed read we run client-side)
// ---------------------------------------------------------------------------
export interface AreaIntel {
  tract: string;
  asOf: number; // ACS 5-year vintage
  medianIncome: number | null;
  population: number | null;
  ownerOcc: number | null;
  renterOcc: number | null;
  renterSharePct: number | null;
  medianGrossRent: number | null;
  source: string;
}

const ACS_YEAR = 2023;
const ACS_VARS = [
  "B19013_001E", // median household income
  "B01003_001E", // total population
  "B25003_001E", // occupied units
  "B25003_002E", // owner-occupied
  "B25003_003E", // renter-occupied
  "B25064_001E", // median gross rent
] as const;

/** Split an 11-digit tract GEOID into ACS's state / county / tract parts. */
export function splitGeoid(geoid: string): { state: string; county: string; tract: string } | null {
  if (!/^\d{11}$/.test(geoid)) return null;
  return { state: geoid.slice(0, 2), county: geoid.slice(2, 5), tract: geoid.slice(5, 11) };
}

export function acsUrl(geoid: string, key: string): string {
  const g = splitGeoid(geoid)!;
  const get = "NAME," + ACS_VARS.join(",");
  // Same-origin proxy (/acs -> api.census.gov/data) so the browser isn't CORS-blocked.
  return (
    `/acs/${ACS_YEAR}/acs/acs5?get=${get}` +
    `&for=tract:${g.tract}&in=state:${g.state}%20county:${g.county}&key=${encodeURIComponent(key)}`
  );
}

/**
 * Pull tract-level demographics & income from Census ACS. Needs a free key
 * (VITE_CENSUS_KEY in dev; the Edge Function holds it in prod). Best-effort:
 * throws on network/CORS/HTTP so the UI can fall back gracefully.
 */
export async function fetchAreaIntel(geoid: string, key?: string): Promise<AreaIntel | null> {
  const g = splitGeoid(geoid);
  const k = key ?? env.VITE_CENSUS_KEY;
  if (!g || !k) return null;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 12000);
  try {
    const res = await fetch(acsUrl(geoid, k), { signal: ctrl.signal });
    if (!res.ok) throw new Error(`ACS HTTP ${res.status}`);
    const rows: string[][] = await res.json();
    const [head, row] = rows;
    const o: Record<string, string> = {};
    head.forEach((h, i) => (o[h] = row[i]));
    const num = (v: string) => (v == null || v === "" || +v < 0 ? null : +v);
    const occ = num(o.B25003_001E);
    const rent = num(o.B25003_003E);
    return {
      tract: geoid,
      asOf: ACS_YEAR,
      medianIncome: num(o.B19013_001E),
      population: num(o.B01003_001E),
      ownerOcc: num(o.B25003_002E),
      renterOcc: rent,
      renterSharePct: occ && rent != null ? Math.round((rent / occ) * 100) : null,
      medianGrossRent: num(o.B25064_001E),
      source: `Census ACS ${ACS_YEAR} 5-yr`,
    };
  } finally {
    clearTimeout(timer);
  }
}

// ---------------------------------------------------------------------------
// FL statewide parcel query builder — exported for the Edge Function.
// Verified working against the live FeatureServer; too slow to hit from the
// browser at scale, so the server calls this, then caches into `data_cache`.
// ---------------------------------------------------------------------------
export const FL_PARCEL_URL =
  "https://services9.arcgis.com/Gh9awoU677aKree0/arcgis/rest/services/Florida_Statewide_Cadastral/FeatureServer/0/query";

export const FL_PARCEL_FIELDS = [
  "PARCEL_ID", "CO_NO", "OWN_NAME", "OWN_ADDR1", "OWN_CITY", "OWN_STATE",
  "PHY_ADDR1", "PHY_CITY", "PHY_ZIPCD", "JV", "AV_SD", "TV_SD", "LND_VAL",
  "JV_HMSTD", "ACT_YR_BLT", "TOT_LVG_AR", "NO_RES_UNT", "DOR_UC",
  "SALE_PRC1", "SALE_YR1", "SALE_PRC2", "SALE_YR2", "CENSUS_BK",
];

/** Point-in-parcel query params (esriGeometryPoint). Small buffer catches
 *  road-centerline geocodes that would otherwise miss the polygon. */
export function flParcelQuery(lng: number, lat: number): string {
  const geometry = JSON.stringify({ x: lng, y: lat, spatialReference: { wkid: 4326 } });
  const params = new URLSearchParams({
    geometry,
    geometryType: "esriGeometryPoint",
    inSR: "4326",
    distance: "40",
    units: "esriSRUnit_Meter",
    spatialRel: "esriSpatialRelIntersects",
    outFields: FL_PARCEL_FIELDS.join(","),
    returnGeometry: "false",
    resultRecordCount: "3",
    f: "json",
  });
  return `${FL_PARCEL_URL}?${params.toString()}`;
}

/** How a fetched parcel maps onto Deal inputs (used by the Edge Function + UI). */
export interface ParcelIntel {
  parcelId: string;
  owner: string;
  absentee: boolean; // owner mailing ≠ situs
  homestead: boolean;
  justValue: number; // market value per the appraiser
  assessedValue: number;
  taxableValue: number;
  landValue: number;
  yearBuilt: number;
  livingArea: number;
  residentialUnits: number;
  useCode: string;
  lastSalePrice: number;
  lastSaleYear: number;
  censusTract: string; // first 11 of CENSUS_BK — ties back to the eligibility module
  source: string;
}
